import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import axios from 'axios';
import { Center, Heading, ListItem, Spinner, Stack, Text, UnorderedList } from '@chakra-ui/react';
import { useAuthStore } from '@/client/services/state/authStore';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useMcpConsentInfo, useMcpMintOauthKey } from '@/client/services/hooks/mcp/useMcpConsent';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

/**
 * OAuth 2.1 consent page for the MCP server (peer of the transition routes —
 * no layout, no auth gate; this page owns the login round-trip). The MCP
 * authorize endpoint redirects here when the user has no identity; after
 * login + an explicit Allow, we mint a single-use oauth_key and send the
 * browser back to /mcp/authorize with the ORIGINAL OAuth params. Deny goes
 * back with `deny=true` and no key, which the API turns into
 * error=access_denied for the client.
 *
 * The authorize origin comes from the consent-info endpoint (trusted, session
 * authenticated) — never from a query param, which an attacker could point at
 * a host of their choosing to capture the oauth_key.
 */
const FORWARDED_PARAMS = [
  'client_id',
  'redirect_uri',
  'response_type',
  'code_challenge',
  'code_challenge_method',
  'scope',
  'state',
] as const;

const REQUIRED_PARAMS = [
  'client_id',
  'redirect_uri',
  'response_type',
  'code_challenge',
  'code_challenge_method',
] as const;

const SCOPE_LABEL_KEYS: Record<string, string> = {
  'mcp:read': 'mcp.consent.scopeRead',
  'mcp:write': 'mcp.consent.scopeWrite',
};

// Only the enumerated OAuth params ride back to authorize — never the raw
// query string, so nothing unexpected can be smuggled onto the redirect.
function buildAuthorizeUrl(
  authorizeUrl: string,
  searchParams: URLSearchParams,
  extra: Record<string, string>,
): string {
  const url = new URL(authorizeUrl);
  FORWARDED_PARAMS.forEach((param) => {
    const value = searchParams.get(param);
    if (value) url.searchParams.set(param, value);
  });
  Object.entries(extra).forEach(([key, value]) => url.searchParams.set(key, value));
  return url.toString();
}

export default function McpConsent() {
  const { t } = useTranslation();
  const { authConfig, logout } = useAuthStore();
  const { setToast } = useToastStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, setRedirectLocation] = useLocalStorage<string | null>('redirectLocation', null);
  const [redirecting, setRedirecting] = useState(false);

  const clientId = searchParams.get('client_id');
  const missingParams = REQUIRED_PARAMS.some((param) => !searchParams.get(param));
  const loggedIn = Boolean(authConfig.authtoken);

  useEffect(() => {
    if (loggedIn || missingParams) return;
    // Peer routes get redirectLocation written by LoggedInLayout; outside it
    // we round-trip ourselves. search keeps the OAuth params alive across
    // the login (successfulLoginActions navigates back here).
    setRedirectLocation(`${window.location.pathname}${window.location.search}`);
    navigate('/login');
  }, [loggedIn, missingParams, navigate, setRedirectLocation]);

  const consentInfo = useMcpConsentInfo(clientId, loggedIn && !missingParams);
  const allowMutation = useMcpMintOauthKey();

  // `loggedIn` above is token PRESENCE; only the API can judge validity. A
  // stored-but-stale token sails past the logged-out effect and consent-info
  // comes back 403 (requiresAuthenticated) — that's a dead session, not an
  // unknown client, so run the same login round-trip instead of an error.
  const consentInfoStatus = axios.isAxiosError(consentInfo.error)
    ? consentInfo.error.response?.status
    : undefined;
  const staleSession =
    consentInfo.isError && (consentInfoStatus === 401 || consentInfoStatus === 403);

  useEffect(() => {
    if (!staleSession) return;
    // Order matters: redirectLocation first — logout() hard-navigates.
    setRedirectLocation(`${window.location.pathname}${window.location.search}`);
    logout();
  }, [staleSession, logout, setRedirectLocation]);

  if (missingParams) {
    return (
      <Center minHeight="100vh">
        <Text as="h4">{t('mcp.consent.invalidRequest')}</Text>
      </Center>
    );
  }

  if (consentInfo.isError && !staleSession) {
    // unknownClient is reserved for a definitive 404 — telling users to
    // restart the connection over a transient blip sends them down the wrong
    // path (and QA down the wrong bug report).
    if (consentInfoStatus === 404) {
      return (
        <Center minHeight="100vh">
          <Text as="h4">{t('mcp.consent.unknownClient')}</Text>
        </Center>
      );
    }
    return (
      <Center minHeight="100vh">
        <Stack spacing={4} alignItems="center">
          <Text as="h4">{t('mcp.consent.loadFailed')}</Text>
          <OutlineButton onClick={() => consentInfo.refetch()} data-testid="mcp-consent-retry">
            {t('mcp.consent.tryAgain')}
          </OutlineButton>
        </Stack>
      </Center>
    );
  }

  const info = consentInfo.data;
  if (!loggedIn || redirecting || !info) {
    return (
      <Center minHeight="100vh">
        <Spinner size="lg" />
      </Center>
    );
  }

  // A malformed authorize_url would otherwise throw inside the click handler and leave both
  // buttons as silent no-ops. Only commit to the spinner once there is somewhere to go.
  const redirectToAuthorize = (extra: Record<string, string>) => {
    let target: string;

    try {
      target = buildAuthorizeUrl(info.authorize_url, searchParams, extra);
    } catch {
      setToast({ show: true, status: 'error', title: t('mcp.consent.redirectFailed') });

      return;
    }

    setRedirecting(true);
    window.location.href = target;
  };

  const onAllow = () => {
    allowMutation.mutate(undefined, {
      onSuccess: (key) => redirectToAuthorize({ oauth_key: key }),
    });
  };

  const onDeny = () => redirectToAuthorize({ deny: 'true' });

  // `||`, not `??`: an explicit `?scope=` is empty but not nullish. buildAuthorizeUrl skips falsy
  // params too, so authorize applies the client's registered scopes — show the same thing.
  const requestedScopes = (searchParams.get('scope') || info.client_scopes.join(' '))
    .split(' ')
    .filter(Boolean);

  return (
    <Center minHeight="100vh" paddingX={{ base: 6, md: 9 }}>
      <Stack spacing={6} maxWidth="480px" width="100%">
        <Heading as="h2" size="md">
          {t('mcp.consent.title', { clientName: info.client_name })}
        </Heading>
        <Text color="neutral.800">
          {t('mcp.consent.description', {
            clientName: info.client_name,
            user: authConfig.user?.email,
          })}
        </Text>
        <Stack spacing={2}>
          <Text fontWeight="semibold">{t('mcp.consent.scopesHeading')}</Text>
          <UnorderedList paddingLeft={4}>
            {requestedScopes.map((scope) => (
              <ListItem key={scope}>
                {SCOPE_LABEL_KEYS[scope] ? t(SCOPE_LABEL_KEYS[scope]) : scope}
              </ListItem>
            ))}
          </UnorderedList>
        </Stack>
        <Stack direction="row" spacing={4}>
          <SolidButton
            onClick={onAllow}
            isLoading={allowMutation.isLoading}
            data-testid="mcp-consent-allow"
          >
            {t('mcp.consent.allow')}
          </SolidButton>
          <OutlineButton onClick={onDeny} data-testid="mcp-consent-deny">
            {t('mcp.consent.deny')}
          </OutlineButton>
        </Stack>
        <Text fontSize="sm" color="neutral.800">
          {t('mcp.consent.redirectNotice')}
        </Text>
      </Stack>
    </Center>
  );
}
